import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { LearningResourceFormDialogComponent } from 'src/app/components/main/employees/rrhh/learning-resource-form-dialog/learning-resource-form-dialog.component';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { LearningResource, LearningResourceSerialized } from 'src/app/models/learning_resource.model';
import { ApiService, UtilsService } from 'src/app/services';

@Component({
  selector: 'app-formations-catalog-tab',
  templateUrl: './formations-catalog-tab.component.html',
  styleUrl: './formations-catalog-tab.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatTableModule,
    MatTooltipModule,
  ],
})
export class FormationsCatalogTabComponent extends IndexTemplateComponent implements OnInit {
  private readonly api_service = inject(ApiService);
  private readonly change_detector_ref = inject(ChangeDetectorRef);
  private readonly mat_dialog = inject(MatDialog);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly snack_bar = inject(MatSnackBar);
  protected readonly form_builder = inject(UntypedFormBuilder);
  protected readonly router = inject(Router);
  protected readonly activated_route = inject(ActivatedRoute);
  protected readonly utils_service = inject(UtilsService);

  dataSource = new MatTableDataSource<LearningResourceSerialized>([]);

  displayedColumns: string[] = ['name', 'duration_in_hours', 'skills', 'actions'];

  override filterFormExtraParams: Record<string, unknown> = {
    search: [''],
  };

  constructor() {
    super(inject(Router), inject(UntypedFormBuilder), inject(ActivatedRoute), inject(UtilsService));
  }

  override ngOnInit(): void {
    this.processUrlParams();

    const url_page = this.activated_route.snapshot.queryParamMap.get('page');
    if (!url_page) {
      this.pageIndex = 0;
    }

    super.ngOnInit();

    this.listenSearchFormControl();
  }

  openCreateDialog(): void {
    const dialog_ref = this.mat_dialog.open(LearningResourceFormDialogComponent, {
      width: '520px',
      data: { mode: 'create' },
    });

    dialog_ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((should_refresh: boolean | undefined): void => {
        if (should_refresh) {
          this.pageIndex = 0;
          this.fetchData();
        }
      });
  }

  openEditDialog(learning_resource: LearningResourceSerialized): void {
    const dialog_ref = this.mat_dialog.open(LearningResourceFormDialogComponent, {
      width: '520px',
      data: { mode: 'edit', learning_resource },
    });

    dialog_ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((should_refresh: boolean | undefined): void => {
        if (should_refresh) {
          this.fetchData();
        }
      });
  }

  deleteLearningResource(learning_resource: LearningResourceSerialized): void {
    if (!confirm(`¿Eliminar la formación "${learning_resource.name}"?`)) {
      return;
    }

    this.api_service
      .deleteLearningResource(learning_resource.id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => {
          if (this.dataSource.data.length === 1 && this.pageIndex > 0) {
            this.pageIndex -= 1;
          }
          this.fetchData();
          this.snack_bar.open('Formación eliminada', undefined, { duration: 3000 });
        },
        error: (err: { error?: { message?: string }; message?: string }): void => {
          const message = err?.error?.message ?? err?.message ?? 'Error al eliminar.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
        },
      });
  }

  override fetchData(): void {
    const search_term = this.filterForm.get('search')?.value || '';

    const params: Record<string, unknown> = {
      page: this.pageIndex + 1,
      per_page: this.pageSize,
      order_by: 'name:asc',
      with: 'skills,learning_resource_platform',
    };

    if (search_term) {
      params.scopes = `search:${search_term}`;
    }

    this.api_service
      .getLearningResourcesPaged(params)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (response: ArrayResponse<LearningResource>): void => {
          this.dataSource.data = this.serializeLearningResources(response.data);
          this.setMetadata(response.meta.total, response.meta.current_page - 1, response.meta.per_page);
          this.change_detector_ref.markForCheck();
        },
      });
  }

  override changePage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.fetchData();
    super.changePage(event);
  }

  private listenSearchFormControl(): void {
    this.filterForm
      .get('search')
      ?.valueChanges.pipe(debounceTime(500), distinctUntilChanged(), takeUntilDestroyed(this.destroy_ref))
      .subscribe((search_term: string): void => {
        if (search_term !== '') {
          this.pageIndex = 0;
          this.fetchData();
        }
      });
  }

  private processUrlParams(): void {
    const url_per_page = this.activated_route.snapshot.queryParamMap.get('per_page');
    if (url_per_page) {
      this.pageSize = parseInt(url_per_page, 10);
    }

    const url_page = this.activated_route.snapshot.queryParamMap.get('page');
    if (url_page) {
      this.pageIndex = parseInt(url_page, 10) - 1;
    }
  }

  private serializeLearningResources(learning_resources: LearningResource[]): LearningResourceSerialized[] {
    return learning_resources.map(learning_resource => ({
      ...learning_resource,
      skills_label: learning_resource.skills?.map(skill => skill.name).join(', ') || '-',
    }));
  }
}
